import { useState, useEffect, useCallback, useMemo, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? getSystemTheme() : mode;
}

function getStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  return 'system';
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);
}

// Track system theme changes via useSyncExternalStore
function subscribeToSystemTheme(callback: () => void) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(getStoredMode);

  // Subscribe to system theme changes so resolvedTheme re-derives
  const systemTheme = useSyncExternalStore(
    subscribeToSystemTheme,
    () => getSystemTheme(),
    () => 'light' as ResolvedTheme,
  );

  // Derive resolvedTheme — no setState needed
  const resolvedTheme = useMemo<ResolvedTheme>(
    () => (mode === 'system' ? systemTheme : mode),
    [mode, systemTheme],
  );

  // Side effect only: apply to DOM + persist (no setState)
  useEffect(() => {
    applyTheme(resolvedTheme);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode, resolvedTheme]);

  const setTheme = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
  }, []);

  // Legacy toggle for simple use cases (cycles: light → dark → light)
  const toggle = useCallback(() => {
    setMode((prev) => {
      const current = resolveTheme(prev);
      return current === 'light' ? 'dark' : 'light';
    });
  }, []);

  return {
    /** The user's preference: 'light' | 'dark' | 'system' */
    mode,
    /** The actual resolved theme: 'light' | 'dark' */
    theme: resolvedTheme,
    /** Set the theme mode */
    setTheme,
    /** Simple toggle between light and dark */
    toggle,
  };
}
